/**
 * ORDEM ALFABÉTICA DE NOMES — a "configuração" única do sistema.
 *
 * Toda lista de pessoas ou de cadastros identificados por nome (usuários,
 * equipamentos, EMMS, departamentos…) é mostrada em ordem crescente A→Z, do
 * mesmo jeito que a agenda do Planejamento: comparação em pt-BR com
 * `sensitivity: "base"` — acento e maiúscula não mudam a posição ("Álvaro"
 * fica junto de "Adolfo", não depois do "Z") — e `numeric: true`, para que
 * "TR-2" venha antes de "TR-10".
 *
 * A tela é a garantia: mesmo que o servidor devolva em outra ordem (id, data
 * de cadastro…), quem exibe ordena aqui.
 *
 * O QUE NÃO PASSA POR AQUI (de propósito): listas por data ou número (análises,
 * OS, remessas, quebras, laudos, histórico) e listas cuja ordem tem significado
 * — ensaios na ordem da bancada (FQ → CR → PCB → 2-FALL), classes de tensão e
 * faixas da NBR 10576, casos da NBR 7274, critérios pela coluna "ordem".
 */

const collator = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

/** Comparador de nomes A→Z. Vazio/nulo vira "" (vai para o começo). */
export const compararNomes = (a: unknown, b: unknown): number =>
  collator.compare(String(a ?? ""), String(b ?? ""));

/**
 * Devolve uma CÓPIA da lista ordenada pelo nome (a original não é tocada —
 * seguro para arrays que outras partes do código leem por índice).
 */
export function ordenarPorNome<T>(lista: readonly T[], nome: (item: T) => unknown): T[] {
  return [...lista].sort((a, b) => compararNomes(nome(a), nome(b)));
}

/**
 * Como `ordenarPorNome`, por CAMPOS do registro e com desempate: ordena pelo
 * 1º campo; empatou, pelo 2º; e assim por diante. Ex.: equipamento por
 * proprietário e, dentro dele, pela TAG (ver ORDEM_EQUIPAMENTO em config.ts).
 */
export function ordenarPorCampos<T extends Record<string, any>>(
  lista: readonly T[],
  campos: readonly string[],
): T[] {
  return [...lista].sort((a, b) => {
    for (const campo of campos) {
      const c = compararNomes(a[campo], b[campo]);
      if (c !== 0) return c;
    }
    return 0;
  });
}
